import React, { type ReactNode } from 'react';

import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import moment from 'moment';
import { ALL_ACCOMMODATION_STATUS_NAMES } from 'src/config/AccommodationStatusNames';
import { type AssociateProfilePlacement, AssociateProfilePlacementType } from 'src/services/associateProfile/types';
import type { RestrictionListParentTask, RestrictionListTask } from 'src/utils/restrictionUtils';

import EmDash from '../emdash';

import ExportActionMenu from './placementActions/exportActions';
import TransferActions from './placementActions/transferActions';
import UpdateActionMenu from './placementActions/updateActions';

interface PlacementCardLayoutProps {
  placement: AssociateProfilePlacement;
  regionTasks?: (RestrictionListTask | RestrictionListParentTask)[];
  children?: ReactNode;
}

const PlacementCardLayout = ({ children, placement, regionTasks }: PlacementCardLayoutProps) => (
  <>
    <Card spacingInset="400 450" minWidth="750px">
      <Column spacing="400">
        <Column spacing="200">
          <Row widths={['fit', 'fit', 'fill']}>
            <Text type="h100" tag="span">
              Case ID:&nbsp;
              <Link type="secondary" href={`/placementDetails/${placement.accommodationId}`}>
                {placement.accommodationType} | {placement.externalCaseId ?? <EmDash />}
              </Link>
            </Text>
            <Row spacing="300">
              {placement.additionalTags.map((tag) => (
                <Tag
                  type={
                    placement.placementType === AssociateProfilePlacementType.ACTIVE &&
                    ALL_ACCOMMODATION_STATUS_NAMES.includes(tag)
                      ? 'success'
                      : 'neutral'
                  }
                >
                  {tag}
                </Tag>
              ))}
            </Row>
            <Row spacing="300" alignmentHorizontal="end">
              {placement.placementType === AssociateProfilePlacementType.TRANSFERRED ? (
                <TransferActions placement={placement} />
              ) : (
                <>
                  <ExportActionMenu placement={placement} />
                  <UpdateActionMenu placement={placement} regionTasks={regionTasks ?? []} />
                </>
              )}
            </Row>
          </Row>
          <Row spacing="200">
            <Text color="secondary">
              Last Updated: {moment(placement.updatedAt).format('MM/DD/YYYY [at] h:mma')} UTC •
            </Text>
            <Link
              href={`https://phonetool.amazon.com/users/${placement.updatedBy}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {placement.updatedBy}@
            </Link>
          </Row>
        </Column>
        <Row spacing="600">{children}</Row>
      </Column>
    </Card>
  </>
);

export default PlacementCardLayout;
